import type { Folder, Note } from "@hq/contracts";
import { buildFolderTree, folderPath, type FolderTreeNode } from "@hq/core";
import type { LocalStore } from "@hq/local-store";
import { displayTitle, effectiveFolderId, sanitizeFileName, uniqueName } from "./lib";

export type ExportFile = { path: string; contents: string };
export type VaultExport = { files: ExportFile[] };

export const EXPORT_JSON_NAME = "hq-export.json";

function yamlScalar(value: string | boolean): string {
  return typeof value === "boolean" ? String(value) : JSON.stringify(value);
}

/** YAML front matter carrying the metadata that does not live in the markdown body. */
export function noteFrontmatter(note: Note, folderNames: readonly string[]): string {
  const fields: [string, string | boolean][] = [
    ["id", note.id],
    ["title", displayTitle(note)],
    ["created", note.createdAt],
    ["updated", note.updatedAt],
    ["folder", folderNames.join("/")],
  ];
  if (note.pinned) fields.push(["pinned", true]);
  return `---\n${fields.map(([k, v]) => `${k}: ${yamlScalar(v)}`).join("\n")}\n---\n`;
}

/** The `.md` file body for one note: front matter followed by the canonical markdown. */
export function noteFileContents(note: Note, folders: readonly Folder[]): string {
  const folderId = effectiveFolderId(note, folders);
  const names = folderId ? folderPath(folders, folderId).map((f) => f.name) : [];
  const body =
    note.markdown.endsWith("\n") || note.markdown === "" ? note.markdown : `${note.markdown}\n`;
  return `${noteFrontmatter(note, names)}\n${body}`;
}

/** `<title>.md`, safe for a filesystem. */
export function noteFileName(note: Pick<Note, "title">): string {
  return `${sanitizeFileName(displayTitle(note))}.md`;
}

/** Directory path per live folder, sanitised and de-duplicated among siblings. */
function folderDirectories(folders: readonly Folder[]): Map<string, string> {
  const out = new Map<string, string>();
  const walk = (nodes: readonly FolderTreeNode[], prefix: string) => {
    const taken = new Set<string>();
    for (const node of nodes) {
      const name = uniqueName(taken, sanitizeFileName(node.folder.name, "Untitled folder"));
      const path = prefix ? `${prefix}/${name}` : name;
      out.set(node.folder.id, path);
      walk(node.children, path);
    }
  };
  walk(buildFolderTree(folders), "");
  return out;
}

/**
 * The whole vault as a folder tree of `.md` files with front matter, the way Obsidian would
 * lay it out. Pure given the store's data; tombstoned notes and folders are skipped.
 */
export async function exportVault(
  store: Pick<LocalStore, "notes" | "folders">,
): Promise<VaultExport> {
  const [notes, folders] = await Promise.all([store.notes.list(), store.folders.all()]);
  const dirs = folderDirectories(folders);
  const takenByDir = new Map<string, Set<string>>();
  const files: ExportFile[] = [];
  const ordered = [...notes].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );
  for (const note of ordered) {
    const folderId = effectiveFolderId(note, folders);
    const dir = folderId ? (dirs.get(folderId) ?? "") : "";
    let taken = takenByDir.get(dir);
    if (!taken) {
      taken = new Set();
      takenByDir.set(dir, taken);
    }
    const name = uniqueName(taken, sanitizeFileName(displayTitle(note)), ".md");
    files.push({
      path: dir ? `${dir}/${name}` : name,
      contents: noteFileContents(note, folders),
    });
  }
  files.sort((a, b) => a.path.localeCompare(b.path));
  return { files };
}

/** Triggers a browser download of `blob` under `name`. */
export function downloadBlob(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// The File System Access API is not in the DOM lib; only the surface we use is declared.
type WritableFile = { write(data: string): Promise<void>; close(): Promise<void> };
type FileHandle = { createWritable(): Promise<WritableFile> };
type DirectoryHandle = {
  getDirectoryHandle(name: string, options: { create: boolean }): Promise<DirectoryHandle>;
  getFileHandle(name: string, options: { create: boolean }): Promise<FileHandle>;
};
type WindowWithPicker = {
  showDirectoryPicker?: (options?: { mode?: "read" | "readwrite" }) => Promise<DirectoryHandle>;
};

export type DownloadExportResult = "directory" | "json" | "cancelled";

/**
 * Writes the export to a folder the user picks (Chromium's File System Access API), or,
 * where that API is missing (Firefox, Safari, WebViews), downloads one
 * `hq-export.json` holding the same `{ files: [{ path, contents }] }` tree.
 * There is no zip library in the bundle, so the fallback is a single JSON file rather than
 * one download per note; a small script can unpack it into `.md` files.
 */
export async function downloadExport(
  files: readonly ExportFile[],
  options: { window?: WindowWithPicker } = {},
): Promise<DownloadExportResult> {
  const w = options.window ?? (globalThis as WindowWithPicker);
  if (typeof w.showDirectoryPicker === "function") {
    let root: DirectoryHandle;
    try {
      root = await w.showDirectoryPicker({ mode: "readwrite" });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return "cancelled";
      throw err;
    }
    for (const file of files) {
      const segments = file.path.split("/");
      const name = segments.pop()!;
      let dir = root;
      for (const segment of segments) dir = await dir.getDirectoryHandle(segment, { create: true });
      const handle = await dir.getFileHandle(name, { create: true });
      const writable = await handle.createWritable();
      await writable.write(file.contents);
      await writable.close();
    }
    return "directory";
  }
  const payload = { version: 1, exportedAt: new Date().toISOString(), files };
  downloadBlob(
    EXPORT_JSON_NAME,
    new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }),
  );
  return "json";
}
